import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-3141" />} data-id="element-3140">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-3142">
        <motion.div variants={itemVariants} data-id="element-3143">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-3145" />} data-id="element-3144" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3146">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-3148" />} data-id="element-3147" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3149">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-3151" />} data-id="element-3150" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3152">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-3154" />} data-id="element-3153" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-3155">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-3156">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3157">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-3158">
            <div className="flex items-center justify-between" data-id="element-3159">
              <div className="flex items-center gap-2" data-id="element-3160">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-3161" />
                <span className="text-sm font-medium text-gray-700" data-id="element-3162">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-3163">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-3164">
              <div className="flex items-center gap-2" data-id="element-3165">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-3166" />
                <span className="text-sm font-medium text-gray-700" data-id="element-3167">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-3168">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-3169" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-3170" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-3171">
            <div className="flex items-center justify-between" data-id="element-3172">
              <div className="flex items-center gap-3" data-id="element-3173">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-3174" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-3175" />}
                <div data-id="element-3176">
                  <div className="text-sm font-medium text-gray-700" data-id="element-3177">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-3178">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-3179">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-3180" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-3181">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-3182">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-3183" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-3184" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-3185">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-3186">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-3187">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-3188">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-3189">
                    <div className="text-lg font-bold text-amber-700" data-id="element-3190">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-3191">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-3192">
                    <div className="text-lg font-bold text-red-700" data-id="element-3193">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-3194">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-3195">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-3196" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-3197" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-3198" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-3199">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-3200">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3201">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3202">
            <Clock className="w-4 h-4" data-id="element-3203" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-3204">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-3205">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-3206">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-3207" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-3208" /> : <Activity className="w-4 h-4" data-id="element-3209" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-3210">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-3211">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-3212">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3213">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3214">
            <Server className="w-4 h-4" data-id="element-3215" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-3216">
            <div className="flex items-center justify-between" data-id="element-3217">
              <span className="text-sm text-gray-600" data-id="element-3218">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-3219">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-3220">
              <span className="text-sm text-gray-600" data-id="element-3221">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-3222">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-3223">
              <div className="flex items-center justify-between mb-1.5" data-id="element-3224">
                <span className="text-sm text-gray-600" data-id="element-3225">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-3226">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-3227">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-3228" />
              </div>
            </div>
            <div data-id="element-3229">
              <div className="flex items-center justify-between mb-1.5" data-id="element-3230">
                <span className="text-sm text-gray-600" data-id="element-3231">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-3232">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-3233">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-3234" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-3235">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3236">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-3237">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-3238">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-3239">
                <div className="flex justify-between text-xs mb-1" data-id="element-3240">
                  <span className="text-gray-600" data-id="element-3241">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-3242">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-3243">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-3244" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3245">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-3246">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-3247" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-3248">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-3249">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-3250">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3251">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-3252">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-3253">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-3254">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3255">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-3256">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-3257" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-3258">
            <div className="flex items-end justify-between" data-id="element-3259">
              <span className="text-2xl font-bold text-gray-900" data-id="element-3260">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-3261">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-3262">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-3263" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-3264">
              <div className="flex justify-between" data-id="element-3265">
                <span className="text-gray-500" data-id="element-3266">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-3267">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3268">
                <span className="text-gray-500" data-id="element-3269">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-3270">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}